import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Phone, ArrowUpRight, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { SITE } from "@/lib/site";
import { path } from "@/lib/site";
import { Reveal } from "../Reveal";
import { supabase } from "@/integrations/supabase/client";
import { contactSchema } from "@/lib/contactSchema";
import { toast } from "sonner";
import type { Lang } from "@/i18n";
import { Breadcrumbs } from "../Breadcrumbs";
import { RelatedLinks } from "../RelatedLinks";
import { Marquee } from "../Marquee";

const TYPE_VALUES = ["operations", "consulting", "tech", "other"] as const;

export function ContactPage({ lang }: { lang?: Lang } = {}) {
  const { t } = useTranslation();
  const opts = t("contact.fields.typeOptions", { returnObjects: true }) as string[];
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const typeIdx = Number(fd.get("typeIdx") ?? 0);
    const payload = {
      name: String(fd.get("name") || ""),
      email: String(fd.get("email") || ""),
      company: String(fd.get("company") || ""),
      message: String(fd.get("message") || ""),
      request_type: TYPE_VALUES[Math.max(0, Math.min(TYPE_VALUES.length - 1, typeIdx))],
      consent: fd.get("consent") === "on" ? (true as const) : (false as never),
    };

    const parsed = contactSchema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }

    setSubmitting(true);
    const submissionId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const { error: insErr } = await supabase.from("contact_submissions").insert({
      id: submissionId,
      name: parsed.data.name,
      email: parsed.data.email,
      company: parsed.data.company || null,
      message: parsed.data.message,
      request_type: parsed.data.request_type,
      consent: parsed.data.consent,
      source_lang: lang ?? null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
    });
    setSubmitting(false);

    if (insErr) {
      if (import.meta.env.DEV) console.error(insErr);
      setError("Une erreur est survenue. Veuillez réessayer.");
      toast.error("Erreur lors de l'envoi");
      return;
    }
    // Fire-and-forget admin email notification — the endpoint validates the
    // submission_id against the row we just created and will only ever send
    // one notification per real submission.
    fetch("/api/public/contact-notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ submission_id: submissionId }),
    }).catch((err) => {
      if (import.meta.env.DEV) console.error("contact-notify failed", err);
    });
    setSent(true);
    toast.success(t("contact.success"));
  };

  return (
    <>
      <Breadcrumbs lang={lang ?? "fr"} current="contact" />
      {/* HERO */}
      <section className="relative overflow-hidden border-b-[1.5px] border-foreground">
        <div className="grain" aria-hidden />
        <div
          aria-hidden
          className="hatch float-y pointer-events-none absolute -top-10 -right-10 h-48 w-48 opacity-20 md:h-72 md:w-72"
        />
        <div className="container-page relative pt-14 pb-16 md:pt-20 md:pb-24">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/60">
              / Contact — 02:00 AM
            </span>
          </Reveal>
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-12">
            <Reveal delay={0.08} className="lg:col-span-8">
              <h1 className="font-display font-black uppercase leading-[0.88] tracking-[-0.045em] text-foreground text-[clamp(2.5rem,8vw,7rem)]">
                {t("contact.h1")}
              </h1>
            </Reveal>
            <Reveal delay={0.14} className="lg:col-span-4">
              <div className="bento-tile h-full p-6 md:p-7">
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                  / Brief
                </span>
                <p className="mt-4 text-base leading-relaxed text-foreground/85">{t("contact.sub")}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee variant="ink" items={["Reply within 48h", "Free 30-min call", "Belgium · Luxembourg"]} />

      <section className="section border-b-[1.5px] border-foreground">
        <div className="container-page grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              / Direct
            </span>
            <div className="mt-6 grid gap-4">
              <ContactRow
                icon={<Mail size={18} />}
                title={t("contact.emailTitle")}
                desc={SITE.email}
                cta={t("common.sendEmail")}
                href={`mailto:${SITE.email}`}
                primary
              />
              <ContactRow
                icon={<Phone size={18} />}
                title={t("contact.phoneTitle")}
                desc={SITE.phone}
                cta={t("common.call")}
                href={`tel:${SITE.phoneRaw}`}
              />
            </div>
          </Reveal>

          <Reveal delay={0.06} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bento-tile tile-fx group p-8 md:p-10"
            >
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                / Form
              </span>
              <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[1] tracking-[-0.03em] md:text-4xl">
                {t("contact.formTitle")}
              </h2>
              <div className="mt-7 space-y-5">
                <Field name="name" label={t("contact.fields.name")} required maxLength={120} />
                <Field
                  name="email"
                  type="email"
                  label={t("contact.fields.email")}
                  required
                  maxLength={200}
                />
                <Field name="company" label={t("contact.fields.company")} maxLength={150} />
                <div>
                  <label className="eyebrow mb-2 block">{t("contact.fields.type")}</label>
                  <select
                    name="typeIdx"
                    defaultValue="0"
                    className="w-full rounded-none border-0 border-b border-[var(--border-strong)] bg-transparent px-0 py-2.5 text-base focus:border-foreground focus:outline-none"
                  >
                    {opts.map((o, i) => (
                      <option key={o} value={i}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="eyebrow mb-2 block">{t("contact.fields.message")}</label>
                  <textarea
                    name="message"
                    required
                    maxLength={2000}
                    rows={5}
                    className="w-full rounded-none border-0 border-b border-[var(--border-strong)] bg-transparent px-0 py-2.5 text-base focus:border-foreground focus:outline-none"
                  />
                </div>
                <label className="flex items-start gap-2.5 text-xs text-foreground/70">
                  <input
                    type="checkbox"
                    name="consent"
                    required
                    className="mt-0.5 accent-[var(--foreground)]"
                  />
                  <span>{t("contact.fields.gdpr")}</span>
                </label>
                {error && <p className="text-sm text-[oklch(0.55_0.20_27)]">{error}</p>}
                {sent ? (
                  <p className="border border-foreground bg-background px-4 py-3 text-sm text-foreground">
                    {t("contact.success")}
                  </p>
                ) : (
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-blaze inline-flex w-full items-center justify-center gap-2 px-7 py-3.5 text-sm font-bold uppercase tracking-[0.12em] disabled:opacity-60"
                  >
                    {submitting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        {t("common.send")} <ArrowUpRight size={16} />
                      </>
                    )}
                  </button>
                )}
                <p className="mt-2 text-center text-xs text-foreground/60">
                  ✓ {t("contact.responseGuarantee")}
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-[var(--border-strong)]">
        <div className="container-page py-12">
          <div className="overflow-hidden border border-[var(--border-strong)]">
            <iframe
              title="2AM Hospitality location"
              src={`https://www.google.com/maps?q=${encodeURIComponent(
                `${SITE.address.street}, ${SITE.address.postal} ${SITE.address.city}`
              )}&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-[360px] w-full grayscale"
              style={{ border: 0 }}
            />
          </div>
        </div>
      </section>
      <RelatedLinks lang={lang ?? "fr"} items={["services", "references", "tools"]} />
    </>
  );
}

function ContactRow({
  icon,
  title,
  desc,
  cta,
  href,
  primary,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  cta: string;
  href: string;
  primary?: boolean;
}) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      className={`bento-tile ${primary ? "is-ink" : ""} tile-fx is-hover group flex items-center justify-between gap-6 p-6 ${
        primary ? "lg:p-8" : ""
      }`}
    >
      <div className="flex items-center gap-5">
        <span className="grid h-12 w-12 shrink-0 place-items-center border-[1.5px] border-current">
          {icon}
        </span>
        <div>
          <h3 className="font-display text-base font-bold uppercase tracking-[-0.01em]">{title}</h3>
          <p className="text-sm opacity-75">{desc}</p>
        </div>
      </div>
      <span className="font-display hidden shrink-0 items-center gap-1.5 pr-1 text-xs font-bold uppercase tracking-[0.14em] sm:inline-flex whitespace-pre-line transition-all group-hover:gap-2.5">
        {cta}
        <ArrowUpRight
          size={14}
          className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </span>
    </a>
  );
}

function Field({
  name,
  label,
  type = "text",
  required,
  maxLength,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  maxLength?: number;
}) {
  return (
    <div>
      <label className="eyebrow mb-2 block">
        {label}
        {required && <span className="ml-1 text-foreground">*</span>}
      </label>
      <input
        name={name}
        type={type}
        required={required}
        maxLength={maxLength}
        className="w-full rounded-none border-0 border-b border-[var(--border-strong)] bg-transparent px-0 py-2.5 text-base focus:border-foreground focus:outline-none"
      />
    </div>
  );
}
